import { downloadBlobInBrowser, invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncConfirmationDialog, NoddiButton, NoddiCard, NoddiInfoBanner } from 'noddi-ui-web';
import { useState } from 'react';
import { useAuthContext } from '../../contexts/useAuthContext';

interface RecoveryCodesManagementProps {
  translations?: {
    recoveryCodes: string;
    description: string;
    enabled: string;
    disabled: string;
    generate: string;
    regenerate: string;
    disable: string;
    remaining: string;
    saveCodesWarning: string;
    copy: string;
    download: string;
  };
}

export const RecoveryCodesManagement = ({ translations }: RecoveryCodesManagementProps) => {
  const { userData } = useAuthContext();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const texts = translations ?? {
    recoveryCodes: 'Recovery codes',
    description:
      'You can use one of your recovery codes to authenticate your account in place of your other multi-factor methods.',
    enabled: 'Enabled',
    disabled: 'Not set up',
    generate: 'Generate recovery codes',
    regenerate: 'Regenerate recovery codes',
    disable: 'Disable',
    remaining: 'codes remaining',
    saveCodesWarning: 'Store these codes in a safe place. They will not be shown again. Each code can be used once.',
    copy: 'Copy',
    download: 'Download'
  };

  const userId = userData?.user.id;

  const {
    data: statusData,
    error: statusError,
    isPending: isStatusPending
  } = noddiAsync.useGet({
    type: URLKeys.getRecoveryCodesStatus,
    input: undefined,
    queryConfig: {
      enabled: !!userId
    }
  });

  const { mutateAsync: generateCodes, isPending: isGenerating } = noddiAsync.usePost({
    type: URLKeys.postGenerateRecoveryCodes,
    queryConfig: {
      onSuccess: (response) => {
        setCodes(response.data.codes);
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getRecoveryCodesStatus });
      },
      onError: () => setError('Failed to generate recovery codes')
    }
  });

  const { mutateAsync: disableCodes, isPending: isDisabling } = noddiAsync.useDelete({
    type: URLKeys.deleteRecoveryCodes,
    queryConfig: {
      onSuccess: () => {
        setCodes(null);
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getRecoveryCodesStatus });
      }
    }
  });

  if (!userId) {
    return null;
  }

  const enabled = statusData?.enabled ?? false;
  const remaining = statusData?.remaining ?? 0;

  const handleCopy = () => {
    if (codes) {
      void navigator.clipboard.writeText(codes.join('\n'));
    }
  };

  const handleDownload = () => {
    if (!codes) {
      return;
    }
    downloadBlobInBrowser(new Blob([codes.join('\n')], { type: 'text/plain' }), 'noddi-recovery-codes.txt');
  };

  return (
    <NoddiCard title={texts.recoveryCodes} description={texts.description}>
      <div className='flex flex-col gap-4'>
        {error && <NoddiInfoBanner variant='error' texts={[{ value: error }]} />}
        {statusError && <NoddiInfoBanner variant='error' texts={[{ value: 'Failed to load recovery codes status' }]} />}

        {!isStatusPending && (
          <div className='flex items-center justify-between rounded-lg border p-3'>
            <div className='flex flex-col'>
              <span className='font-medium'>{enabled ? texts.enabled : texts.disabled}</span>
              {enabled && (
                <span className='text-sm text-muted-foreground'>
                  {remaining} {texts.remaining}
                </span>
              )}
            </div>
            {enabled && (
              <NoddiAsyncConfirmationDialog
                title={texts.disable}
                description='Disabling recovery codes will invalidate all remaining codes. Are you sure?'
                trigger={
                  <NoddiButton variant='destructive' size='small'>
                    {texts.disable}
                  </NoddiButton>
                }
                confirmAction={async () => {
                  await disableCodes({});
                }}
                confirmActionLoading={isDisabling}
              />
            )}
          </div>
        )}

        {codes && (
          <div className='flex flex-col gap-3 rounded-lg border p-4'>
            <NoddiInfoBanner variant='warning' texts={[{ value: texts.saveCodesWarning }]} />
            <div className='grid grid-cols-2 gap-2 font-mono text-sm'>
              {codes.map((code) => (
                <span key={code} className='rounded-sm bg-muted px-2 py-1'>
                  {code}
                </span>
              ))}
            </div>
            <div className='flex gap-2'>
              <NoddiButton variant='outline' size='small' onClick={handleCopy}>
                {texts.copy}
              </NoddiButton>
              <NoddiButton variant='outline' size='small' onClick={handleDownload}>
                {texts.download}
              </NoddiButton>
            </div>
          </div>
        )}

        <div>
          <NoddiAsyncConfirmationDialog
            title={enabled ? texts.regenerate : texts.generate}
            description={
              enabled
                ? 'Regenerating will invalidate your existing recovery codes. Are you sure?'
                : 'A new set of recovery codes will be generated and shown once.'
            }
            trigger={
              <NoddiButton loading={isGenerating} disabled={isGenerating}>
                {enabled ? texts.regenerate : texts.generate}
              </NoddiButton>
            }
            confirmAction={async () => {
              await generateCodes({});
            }}
            confirmActionLoading={isGenerating}
          />
        </div>
      </div>
    </NoddiCard>
  );
};
